import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { SignificanceSettings } from "types/significance.types"
import {
  Catalogue,
  CatalogueInstance,
  CatalogueQuestion,
  findInstance,
  findQuestion,
} from "../types/catalogue"
import {
  Banner,
  BaseRule,
  isMeanStatistic,
  isRangeRow,
  MEAN_STAT_TYPES,
  MeanStat,
  RowItem,
  SortRule,
  SummaryChoice,
  TableSpec,
  TabSpecDocument,
} from "../types/tabSpecDocument"
import { defaultRows } from "./defaults"
import { defaultScores } from "./scale"
import { ShownNames, shownNames } from "./shownLabels"
import { legacySummary, sliceWords } from "./summaries"

/** A table with every default filled in: what the compiler and the screens read. */
export interface ResolvedTable {
  table: TableSpec
  /**
   * null = the question is no longer in the questionnaire; for a table from
   * the data, the variable is not in the prepared data or the data is not loaded.
   */
  question: CatalogueQuestion | null
  /** null for a grid summary, and when the instance is gone. */
  instance: CatalogueInstance | null
  banner: Banner | null
  base: BaseRule
  /** undefined = off. */
  significance: SignificanceSettings | undefined
  /**
   * Without the table's own list: a numeric table's are the defaults', with
   * the engine's statistics of `meanStats` after them when they hold the
   * mean; a grid summary of means shows the mean and those statistics for
   * each grid row (never the mean excluding 0).
   */
  statistics: STATISTIC_TYPE[]
  /**
   * What is shown under the mean, in display order: the table's, else the
   * defaults'. A numeric table shows its statistics in `statistics` and reads
   * only the mean excluding 0 here: with a list of its own, from its own
   * `meanStats` alone; following the defaults, while they show the mean.
   */
  meanStats: MeanStat[]
  rows: RowItem[]
  scores: Record<string, number> | null
  /**
   * What each grid row of a grid summary shows: its own `summary`, else the
   * one the defaults gave before summaries had their own (`legacySummary`).
   * null for every other table.
   */
  summary: SummaryChoice | null
  /** The order of the answer rows (a grid summary's rows): the table's, else the defaults', else "none". */
  sort: SortRule
  /** Answer rows nobody gave are left out: the table's, else the defaults', else false. */
  hideEmpty: boolean
  /**
   * The question, and its grid row or loop, as the tables name it; a grid
   * summary fixed at some values (`slice`) names them ("Spend grid - Online").
   */
  title: string
}

const withNewOptions = (stored: RowItem[], q: CatalogueQuestion): RowItem[] => {
  const present = new Set(
    stored.flatMap((r) => (r.kind === "option" ? [r.label] : []))
  )
  const added: RowItem[] = q.options
    .filter((o) => !present.has(o.label))
    .map((o) => ({ kind: "option", label: o.label }))
  return [...stored, ...added]
}

/**
 * `names`: the shown labels the title uses (the spec's own by default; the
 * compiler passes one tracking `ShownNames` through every step of a plan).
 */
export const resolveTable = (
  table: TableSpec,
  spec: TabSpecDocument,
  catalogue: Catalogue,
  names: ShownNames = shownNames(spec.labels)
): ResolvedTable => {
  const d = spec.defaults
  const question = findQuestion(catalogue, table.question, table.source)
  const instance =
    question && table.kind !== "grid-summary"
      ? findInstance(question, table.instance)
      : null
  const bannerId = table.bannerId ?? d.bannerId
  const banner = spec.banners.find((b) => b.id === bannerId) ?? null
  const picked =
    table.significance === "off" ? undefined : (table.significance ?? d.significance)
  const significance = picked && picked.enabled ? picked : undefined
  const scores = table.scores ?? (question ? defaultScores(question) : null)
  const summary =
    table.kind !== "grid-summary" ? null : (table.summary ?? legacySummary(question, d))
  const chosenMeanStats = table.meanStats ?? d.meanStats ?? []
  // The statistics under the mean go only where the mean is: after the
  // defaults' list for numbers when it holds the mean.
  const numericMean = d.statistics.numeric.includes(STATISTIC_TYPE.AVG)
  // A number table with a list of its own shows that list, and the mean
  // excluding 0 only by its own `meanStats`: the defaults' join the defaults'
  // list alone, the mean excluding 0 too.
  const underMean =
    table.kind !== "numeric"
      ? chosenMeanStats
      : table.statistics !== undefined
        ? (table.meanStats ?? [])
        : numericMean
          ? chosenMeanStats
          : []
  const meanStats = MEAN_STAT_TYPES.filter((s) => underMean.includes(s))
  // The engine's statistics among them; the mean excluding 0 is no statistic.
  const meanStatistics = meanStats.filter(isMeanStatistic)
  const numericDefaults = numericMean
    ? [...d.statistics.numeric, ...meanStatistics.filter((s) => !d.statistics.numeric.includes(s))]
    : d.statistics.numeric
  const statistics =
    table.statistics ??
    (table.kind === "numeric"
      ? numericDefaults
      : summary?.kind === "mean"
        ? // A summary of means shows no mean excluding 0.
          [STATISTIC_TYPE.AVG, ...meanStatistics]
        : d.statistics.categorical)
  // A number table's rows are its ranges, none by default; a range is a row
  // of a number table only.
  const rows =
    table.kind === "numeric"
      ? (table.rows ?? []).filter(isRangeRow)
      : (!question
          ? (table.rows ?? [])
          : table.rows
            ? withNewOptions(table.rows, question)
            : defaultRows(table.kind, question, d, scores)
        ).filter((r) => !isRangeRow(r))
  // Hidden and data questions have no text: their title is the label alone.
  // A table's own title comes first, then the shown labels, then the raw ones.
  const text = question?.text.trim() ?? ""
  const head = !question
    ? names.variable(table.question)
    : text
      ? `${names.variable(question.label)}. ${text}`
      : names.variable(question.label)
  // A grid summary fixed at some values names them.
  const fixed =
    question && table.kind === "grid-summary" && table.slice
      ? sliceWords(question, table.slice, names)
      : ""
  const title =
    table.title ??
    (question && instance && instance.label
      ? `${head} - ${names.instance(question.label, instance.id, instance.label)}`
      : fixed
        ? `${head} - ${fixed}`
        : head)
  return {
    table,
    question,
    instance,
    banner,
    base: table.base ?? d.base,
    significance,
    statistics,
    meanStats,
    rows,
    scores,
    summary,
    sort: table.sort ?? d.sort ?? "none",
    hideEmpty: table.hideEmpty ?? d.hideEmpty ?? false,
    title,
  }
}
